import { ChefHat, Package, Layers, LayoutGrid, Hammer, Wrench } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useScrollReveal, revealClass } from '../hooks/useScrollReveal';

const icons = [ChefHat, Package, Layers, LayoutGrid, Hammer, Wrench];

export default function Services() {
  const { t } = useLang();
  const { ref, isVisible } = useScrollReveal();

  const services = [1, 2, 3, 4, 5, 6].map((n) => ({
    icon: icons[n - 1],
    title: t(`service_${n}_title`),
    desc: t(`service_${n}_desc`),
  }));

  return (
    <section id="servicos" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={revealClass(isVisible)}>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#C2410C] mb-4">
            <span className="w-6 h-px bg-[#C2410C]" />
            {t('services_label')}
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 tracking-tight mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="services-title"
          >
            {t('services_title')}
          </h2>
          <p className="text-base sm:text-lg text-zinc-500 max-w-2xl mb-16">
            {t('services_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => {
            const Icon = service.icon;
            return (
              <ServiceCard
                key={i}
                icon={Icon}
                title={service.title}
                desc={service.desc}
                index={i}
                data-testid={`service-card-${i}`}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ icon: Icon, title, desc, index }) {
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <div
      ref={ref}
      className={`group bg-white border border-zinc-200 rounded-xl p-7 hover:-translate-y-1.5 hover:shadow-xl hover:border-zinc-300 transition-all duration-300 ${revealClass(isVisible)}`}
      style={{ transitionDelay: `${index * 80}ms` }}
      data-testid={`service-card-${index}`}
    >
      <div className="w-12 h-12 bg-[#C2410C]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#C2410C]/15 transition-colors duration-300">
        <Icon className="text-[#C2410C]" size={22} strokeWidth={1.75} />
      </div>
      <h3
        className="text-lg font-bold text-zinc-900 mb-2"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {title}
      </h3>
      <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
      <div className="mt-5 w-8 h-0.5 bg-[#C2410C] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  );
}
